import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { getSafeMcpReturnTo } from '../utils/mcpAuthorization.ts';
import { requestPasswordRecovery, verifyPasswordRecovery, resetRecoveredPassword } from '../services/apiPasswordRecovery.ts';

type Step = 'email' | 'code' | 'password' | 'done';
const titles: Record<Step, string> = {
  email: 'Recupere sua senha', code: 'Confirme seu e-mail',
  password: 'Crie uma nova senha', done: 'Senha redefinida com sucesso',
};

export default function PasswordRecovery() {
  const { isDark } = useTheme();
  const location = useLocation();
  const returnTo = getSafeMcpReturnTo(location.search);
  const loginUrl = returnTo ? `/?returnTo=${encodeURIComponent(returnTo)}` : '/';
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  // A autorização e as senhas ficam somente na memória desta tela.
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retryAt, setRetryAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const submitting = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => { heading.current?.focus(); }, [step]);
  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    tick();
    if (!retryAt) return;
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  const beginAgain = () => {
    setStep('email'); setCode(''); setChallenge(''); setResetToken('');
    setPassword(''); setConfirmation(''); setError(''); setNotice('');
  };

  async function perform(action: 'request' | 'verify' | 'reset') {
    if (submitting.current) return;
    if (action === 'request' && retryAt > Date.now()) return;
    if (action === 'verify' && !/^\d{6}$/.test(code)) {
      setError('Informe o código de 6 dígitos recebido por e-mail.'); return;
    }
    if (action === 'reset') {
      if (password.length < 12 || new Blob([password]).size > 72 || password.trim() !== password) {
        setError('Use pelo menos 12 caracteres, sem espaços no início ou no fim. Se a senha for muito longa, reduza seu tamanho.'); return;
      }
      if (password !== confirmation) { setError('As senhas precisam ser iguais. Confira e tente novamente.'); return; }
    }
    submitting.current = true; setBusy(true); setError(''); setNotice('');
    try {
      if (action === 'request') {
        const result = await requestPasswordRecovery(email.trim());
        setChallenge(result.challenge_id); setCode(''); setResetToken('');
        setRetryAt(Date.now() + Math.max(0, result.retry_after_seconds || 60) * 1000);
        setNotice('Se houver uma conta elegível com esse e-mail, você receberá um código. Confira também a pasta de spam.');
        setStep('code');
      } else if (action === 'verify') {
        const result = await verifyPasswordRecovery(challenge, code);
        setResetToken(result.reset_token); setCode(''); setChallenge(''); setStep('password');
      } else {
        await resetRecoveredPassword(resetToken, password, confirmation);
        setResetToken(''); setPassword(''); setConfirmation(''); setEmail(''); setStep('done');
      }
    } catch (failure) {
      const response = (failure as { response?: { status?: number; headers?: Record<string, string> } })?.response;
      const status = response?.status;
      if (status === 429) {
        const seconds = Number(response?.headers?.['retry-after']);
        if (action === 'request') setRetryAt(Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : 60) * 1000);
        setError('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
      } else if (action === 'verify' && [400, 401, 403, 404, 410].includes(status || 0)) {
        setCode(''); setError('Código inválido, expirado ou já utilizado. Confira o código ou solicite outro.');
      } else if (action === 'reset' && [400, 401, 403, 404, 410].includes(status || 0)) {
        beginAgain();
        setError('Esta autorização expirou ou já foi utilizada. Se você já salvou a senha, entre com ela; caso contrário, solicite outro código.');
      } else if (status === 422) {
        setError(action === 'reset' ? 'Confira a nova senha e a confirmação. Use uma senha diferente da anterior.' : 'Confira os dados informados e tente novamente.');
      } else {
        setError(action === 'reset'
          ? 'Não foi possível confirmar a alteração. Se ela foi concluída, entre com a nova senha. Caso contrário, tente novamente em alguns instantes.'
          : 'Não foi possível concluir agora. Tente novamente em alguns instantes.');
      }
    } finally { submitting.current = false; setBusy(false); }
  }

  const inputClass = `mt-2 min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'border-sminy-text-secondary bg-[#0F1D31] text-white' : 'border-sminy-border bg-white text-sminy-text-primary'}`;
  const linkClass = 'inline-flex min-h-[44px] items-center justify-center text-sm text-sminy-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50';
  return <main className={`flex min-h-[100dvh] items-center justify-center px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] font-sans ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-sminy-canvas text-sminy-text-primary'}`}>
    <section aria-labelledby="recovery-title" className={`w-full max-w-md space-y-5 rounded-xl border p-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-sminy-border bg-white'}`}>
      {step === 'done' ? <CheckCircle2 aria-hidden="true" className="text-sminy-primary" /> : <KeyRound aria-hidden="true" className="text-sminy-primary" />}
      <h1 id="recovery-title" ref={heading} tabIndex={-1} className="text-xl font-semibold outline-none">{titles[step]}</h1>
      {step !== 'done' && <p className="text-sm">Etapa {step === 'email' ? 1 : step === 'code' ? 2 : 3} de 3</p>}
      {notice && <p role="status" className="text-sm">{notice}</p>}
      {step === 'done' ? <p role="status" className="text-sm">Entre com seu e-mail e a nova senha. Por segurança, as sessões anteriores foram encerradas.</p> : <form className="space-y-4" aria-busy={busy} onSubmit={event => { event.preventDefault(); void perform(step === 'email' ? 'request' : step === 'code' ? 'verify' : 'reset'); }}>
        {step === 'email' && <>
          <p className="text-sm">Informe o e-mail da sua conta para receber um código de confirmação.</p>
          <label className="block text-sm" htmlFor="recovery-email">E-mail da conta</label>
          <input id="recovery-email" className={inputClass} type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} maxLength={254} required disabled={busy} value={email} onChange={event => setEmail(event.target.value)} />
        </>}
        {step === 'code' && <>
          <p className="break-words text-sm">Digite o código de 6 dígitos enviado para <strong>{email.trim()}</strong>. Ele expira em 10 minutos.</p>
          <label className="block text-sm" htmlFor="recovery-code">Código de confirmação</label>
          <input id="recovery-code" className={inputClass} type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" minLength={6} maxLength={6} required disabled={busy} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} />
        </>}
        {step === 'password' && <>
          <p className="text-sm">E-mail confirmado. Use pelo menos 12 caracteres. Você não precisa informar a senha atual.</p>
          <label className="block text-sm">Nova senha<input className={inputClass} type="password" autoComplete="new-password" minLength={12} maxLength={72} required disabled={busy} value={password} onChange={event => setPassword(event.target.value)} /></label>
          <label className="block text-sm">Confirmar nova senha<input className={inputClass} type="password" autoComplete="new-password" minLength={12} maxLength={72} required disabled={busy} value={confirmation} onChange={event => setConfirmation(event.target.value)} /></label>
        </>}
        {error && <p role="alert" className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>}
        <button type="submit" disabled={busy || (step === 'email' && remaining > 0)} className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-3 text-sm font-medium text-white hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:opacity-50">
          {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
          {busy ? 'Aguarde...' : step === 'email' ? (remaining ? `Aguarde ${remaining}s` : 'Enviar código') : step === 'code' ? 'Confirmar código' : 'Salvar nova senha'}
        </button>
        {step === 'code' && <div className="flex flex-wrap justify-between gap-2">
          <button className={linkClass} type="button" disabled={busy || remaining > 0} onClick={() => void perform('request')}>{remaining ? `Reenviar em ${remaining}s` : 'Reenviar código'}</button>
          <button className={linkClass} type="button" disabled={busy} onClick={beginAgain}>Corrigir e-mail</button>
        </div>}
      </form>}
      <Link to={loginUrl} className={linkClass}>{step === 'done' ? 'Entrar na minha conta' : 'Voltar ao login'}</Link>
    </section>
  </main>;
}
